/* eslint-disable @typescript-eslint/no-use-before-define */
import React from 'react';
import { pluck } from 'ramda';
import { Button, Graphic, Icon, MarkdownHTML, Modal } from '../..';
import { ReactComponent as RiverLogo } from '../../../assets/images/image_river_logo.svg';
import ImageInPersonVisit from '../../../assets/images/image_demo_in_person_visit.svg';
import ImageVirtualCare from '../../../assets/images/image_demo_virtual_care.svg';
import ImageBehavioralHealth from '../../../assets/images/image_demo_behavioral_health.svg';
import ImageFamilyPlan from '../../../assets/images/image_demo_family_plan.svg';
import ImagePrescriptionDelivery from '../../../assets/images/image_demo_prescription_delivery.svg';
import ImageWellnessService from '../../../assets/images/image_demo_wellness_service.svg';
import ImageImagingTesting from '../../../assets/images/image_demo_imaging.png';
import ImageLabTesting from '../../../assets/images/image_demo_lab_testing.svg';
import ImageBenefitSummary from '../../../assets/images/image_demo_benefits_summary.png';
import { IconSprite } from '../../Icon/Icon';
import capitalize from '../../../utils/capitalize';

type ModalProps = {
  showModal: boolean;
  isAlreadyEnrolled?: boolean;
  plan?: {
    tier: 'complete' | 'virtual' | 'essential' | 'legacy';
    mode: 'individual' | 'family' | 'partner' | 'children';
  } | null;
  sponsor?: string;
  isAFamilyDependent: boolean;
  onCloseModal: () => void;
};

function WelcomePackage({
  onCloseModal,
  showModal,
  isAlreadyEnrolled,
  plan,
  sponsor,
  isAFamilyDependent,
}: ModalProps) {
  const [screen, setScreen] = React.useState(1);
  const [currentTutorial, setCurrentTutorial] = React.useState('INTRO');
  const [demoStep, setDemoStep] = React.useState(0);

  const planTier = (
    plan?.tier ? (plan.tier === 'essential' ? 'essentials' : plan?.tier) : 'essentials'
  ) as 'essentials' | 'virtual' | 'complete' | 'legacy';

  const PLAN_BENEFIT_SUMMARIES =
    BENEFIT_SUMMARIES[
      (['legacy', 'essentials'].includes(planTier) ? 'essentials' : planTier) as
        | 'essentials'
        | 'virtual'
        | 'complete'
    ];

  const [demo, setDemo] = React.useState(PLAN_BENEFIT_SUMMARIES[0]);
  const currentDemoIndex = PLAN_BENEFIT_SUMMARIES.findIndex(
    fndDemo => fndDemo.topic === demo.topic,
  );

  const tutorialScreens = ['INTRO', 'DEMOS', 'CONTACT', 'SUMMARY']; // INTRO, DEMOS, CONTACT, SUMMARY

  const totalScreens = pluck('steps', PLAN_BENEFIT_SUMMARIES).flat(1).length + 3;

  const getReadablePlanName = (tier?: string) => {
    switch (tier) {
      case 'complete':
        return 'Complete Plan';
      case 'essential':
        return 'Essentials Plan';
      case 'legacy':
        return 'Primary Plan';
      case 'virtual':
        return 'Virtual Plan';
      default:
        return 'Essentials Plan';
    }
  };

  const handleGoBack = () => {
    setScreen(fndScreen => fndScreen - 1);

    if (currentTutorial === 'DEMOS') {
      if (demoStep > 0) {
        setDemoStep(fndDemoStep => fndDemoStep - 1);
        return;
      }

      if (currentDemoIndex > 0) {
        setDemo(PLAN_BENEFIT_SUMMARIES[currentDemoIndex - 1]);
        setDemoStep(PLAN_BENEFIT_SUMMARIES[currentDemoIndex - 1].steps.length - 1);
      } else {
        setCurrentTutorial('INTRO');
      }

      return;
    }

    const findIndex = tutorialScreens.findIndex(
      fndTutorialScreen => fndTutorialScreen === currentTutorial,
    );

    const nextTutorial = tutorialScreens.at(findIndex < 0 ? 0 : findIndex - 1);

    setCurrentTutorial(nextTutorial || currentTutorial);
  };

  const handleCloseModal = () => {
    onCloseModal();
    setScreen(1);
    setCurrentTutorial('INTRO');
    setDemo(PLAN_BENEFIT_SUMMARIES[0]);
    setDemoStep(0);
  };

  const handleTutorialSwitch = () => {
    if (currentTutorial === 'SUMMARY') {
      let docLink = 'https://drive.google.com/file/d/1slFJbg9pHmVW8k0NsNJ9TBBXqLD1cAud/view';

      if (plan?.tier === 'essential' || plan?.tier === 'legacy') {
        docLink = 'https://drive.google.com/file/d/1slFJbg9pHmVW8k0NsNJ9TBBXqLD1cAud/view';
      }

      if (plan?.tier === 'virtual') {
        docLink = 'https://drive.google.com/file/d/1eNjE_1KIxnTM3bcWxvO3FOs1mrjtaJBj/view';
      }

      if (plan?.tier === 'complete') {
        docLink = 'https://drive.google.com/file/d/1bdBmrqaNS2YJF0MKmdYZStsGIAXYHMub/view';
      }

      window.open(docLink, '_blank');
      handleCloseModal();
      return;
    }

    setScreen(fndScreen => fndScreen + 1);

    if (currentTutorial === 'DEMOS') {
      if (demo.steps.at(demoStep + 1)) {
        setDemoStep(fndDemoStep => fndDemoStep + 1);
        return;
      }

      if (PLAN_BENEFIT_SUMMARIES[currentDemoIndex + 1]) {
        setDemo(PLAN_BENEFIT_SUMMARIES[currentDemoIndex + 1]);
        setDemoStep(0);
        return;
      }
    }

    const findIndex = tutorialScreens.findIndex(
      fndTutorialScreen => fndTutorialScreen === currentTutorial,
    );

    const nextTutorial = tutorialScreens.at(findIndex < 0 ? 0 : findIndex + 1);

    setCurrentTutorial(nextTutorial || currentTutorial);
  };

  return (
    <Modal show={showModal} onClose={isAlreadyEnrolled ? () => handleCloseModal() : undefined}>
      <div className="w-full relative overflow-hidden h-full rounded-[18px] laptop:w-[1000px] tablet:h-[650px] bg-[#F6F6F6]">
        <div className="relative flex flex-col w-full h-full px-8 pt-4 pb-9">
          <div className="flex flex-row mb-4 items-center">
            {currentTutorial === 'INTRO' ? (
              <RiverLogo className="flex w-[50px] z-10" />
            ) : (
              <button
                onClick={handleGoBack}
                type="button"
                className="tablet:flex flex-row items-center z-10 mt-[4px]"
              >
                <Icon id="goBack" className="w-8 h-8" />
              </button>
            )}

            <div className="ml-auto text-[13px]">
              <span className="font-semibold">{screen}/</span>
              <span className="text-[#8F969A]">{totalScreens}</span>
            </div>

            <div>
              <IconSprite
                id="icon-demo-out-starsmallies"
                className="w-[13px] h-[14px] absolute right-[20%] top-[10px]"
              />

              <IconSprite
                id="icon-demo-trismallies"
                className={`hidden tablet:block w-[15px] h-auto absolute right-[35%] top-[80px] ${
                  currentTutorial === 'INTRO' ? '' : 'opacity-0'
                }`}
              />

              <IconSprite
                id="icon-demo-starsmallies"
                className={`w-[14px] h-auto absolute left-[25%] top-[50px] ${
                  currentTutorial === 'INTRO' ? '' : 'opacity-0'
                }`}
              />

              <IconSprite
                id="icon-demo-zigsmallies"
                className={`w-[23px] h-[16px] absolute left-[50%] top-[20px] ${
                  currentTutorial === 'INTRO' ? '' : 'opacity-0'
                }`}
              />

              <Graphic
                id="graphicDemoMaskBg01"
                className={`absolute left-0 ${
                  currentTutorial === 'INTRO' ? 'top-0' : '-top-[50px] opacity-50'
                }`}
              />

              <Graphic
                id="graphicDemoMaskBg02"
                className={`hidden tablet:block absolute  left-[20%] ${
                  currentTutorial === 'INTRO' ? 'top-0' : '-top-[10px] opacity-50'
                }`}
              />

              <Graphic
                id="graphicDemoMaskBg03"
                className={`hidden tablet:block absolute right-0 ${
                  currentTutorial === 'INTRO' ? 'top-0' : '-top-[70px] opacity-90'
                }`}
              />
            </div>
          </div>

          {currentTutorial === 'INTRO' && (
            <div
              className={`flex flex-col ${
                isAlreadyEnrolled ? 'pt-10' : 'justify-center'
              }  items-center flex-1 text-center mobile:!py-24`}
            >
              {isAlreadyEnrolled && (
                <>
                  <div>
                    <span className="text-river-deep-orange text-[13px] font-semibold">
                      ACTIVE PLAN
                    </span>

                    <h2 className="font-medium text-stone-500 text-[20px] tracking-[0.04em] leading-8">
                      {`River Health ${getReadablePlanName(plan?.tier)}`}
                    </h2>

                    {sponsor && (
                      <div className="relative w-fit mx-auto mt-0">
                        <span className="font-medium inline-flex text-sm leading-none">
                          {sponsor}
                        </span>
                      </div>
                    )}

                    <p className="font-semibold text-sm">
                      {isAFamilyDependent
                        ? 'Family Dependent'
                        : plan?.mode && plan?.mode !== 'individual'
                        ? `Individual + ${capitalize(plan?.mode)}`
                        : ''}
                    </p>
                  </div>

                  <hr className="w-[38%] border-river-deep-orange border-t-[1.3px] mt-3 mb-5" />
                </>
              )}

              <h1
                className="font-semibold text-river-deep-orange text-[34px] tablet:text-[40px] 
                leading-[40px] tablet:leading-[56px] tracking-[0.032em] mb-3"
              >
                {!isAlreadyEnrolled
                  ? 'Your enrollment is complete.'
                  : `${getReadablePlanName(plan?.tier)} Benefit Summary`}
              </h1>

              <p className="w-full tablet:w-[50%] mx-auto text-[20px] leading-relaxed">
                {!isAlreadyEnrolled
                  ? `Next, we will guide you through the benefits included in your River Health ${getReadablePlanName(
                      plan?.tier,
                    )}.`
                  : `A brief guide to the benefits included in your ${getReadablePlanName(
                      plan?.tier,
                    )}.`}
              </p>
            </div>
          )}

          {currentTutorial === 'DEMOS' && demo && (
            <div className="flex flex-col justify-center items-center flex-1 mobile:pt-20 mobile:pb-20">
              <h3 className="font-semibold text-river-deep-orange text-xl mb-6 tablet:mb-10">
                {demo.topic}
              </h3>

              <div className="flex flex-row w-full items-center justify-center tablet:w-[80%] mx-auto space-x-10">
                <div className="w-full tablet:w-[50%]">
                  {demo.steps.at(demoStep)?.title && (
                    <h4 className="text-[#0E86C7] font-semibold text-lg mb-2">
                      {demo.steps.at(demoStep)?.title}
                    </h4>
                  )}

                  {demo.steps.at(demoStep)?.features ? (
                    <div className="space-y-2 mb-6">
                      {demo.steps.at(demoStep)?.features?.map((feature, index) => (
                        <p className="flex items-baseline" key={index}>
                          {feature.inclusive !== undefined && (
                            <span
                              className={`w-5 h-5 rounded-full mr-3 shrink-0 flex items-center justify-center ${
                                feature.inclusive ? 'bg-[#72C16D]' : 'bg-[#fdba74]'
                              }`}
                            >
                              <Icon
                                id={feature.inclusive ? 'checkmark' : 'info'}
                                className={`  ${
                                  feature.inclusive
                                    ? 'text-white w-[10px] h-[10px]'
                                    : 'text-black w-[18px] h-[18px]'
                                }`}
                              />
                            </span>
                          )}
                          <MarkdownHTML className="w-package-text-alt" content={feature.text} />
                        </p>
                      ))}
                    </div>
                  ) : null}
                </div>

                <div className="hidden tablet:block flex-1">
                  <img
                    src={demo.steps.at(demoStep)?.graphic}
                    alt="error graphic"
                    className="w-auto mx-auto"
                  />
                </div>
              </div>
            </div>
          )}

          {currentTutorial === 'CONTACT' && (
            <div className="flex flex-col pt-14 items-center flex-1 text-center mobile:pt-10 mobile:pb-16">
              <h3 className="font-semibold text-river-deep-orange text-xl mb-3">How to Get Help</h3>

              <div className="text-[17px] mb-6">
                Have questions? Need help using our services? We’re here for you!
              </div>

              <div>
                <p className="text-[15px] font-semibold mb-10">
                  Our office hours are 8am - 5pm CT on Monday - Friday.
                </p>

                <div
                  className="flex flex-row flex-wrap justify-center items-center space-y-2 
                  tablet:space-y-0 tablet:space-x-6"
                >
                  <div>
                    <div
                      className="hidden tablet:flex justify-center items-center bg-white w-16 h-16 border-[0.5px] 
                      border-river-deep-orange/50 rounded-full mx-auto mb-2"
                    >
                      <IconSprite
                        id="icon-help-phone"
                        className="w-[37px] h-[39px] text-river-deep-orange"
                      />
                    </div>
                    <a
                      href="tel:(888) 814-6062"
                      className="text-river-deep-orange underline underline-offset-2"
                    >
                      (888) 814-6062
                    </a>
                    <p className="text-[15px]">Call / Text</p>
                  </div>

                  <div>
                    <div
                      className="hidden tablet:flex justify-center items-center bg-white w-16 h-16 border-[0.5px] 
                    border-river-deep-orange/50 rounded-full mx-auto mb-2"
                    >
                      <IconSprite id="icon-help-email" className="w-[37px] h-[39px]" />
                    </div>
                    <a
                      href="mailto:team@helloriver.com"
                      className="text-river-deep-orange underline underline-offset-2"
                    >
                      team@helloriver.com
                    </a>
                    <p className="text-[15px]">Email us</p>
                  </div>

                  <div>
                    <div
                      className="hidden tablet:flex justify-center items-center bg-white w-16 h-16 border-[0.5px] 
                      border-river-deep-orange/50 rounded-full mx-auto mb-2"
                    >
                      <IconSprite
                        id="icon-help-headset"
                        className="w-[37px] h-[39px] text-[#F6914F]"
                      />
                    </div>
                    <a
                      href="https://www.river.health/help-center/home"
                      target="_blank"
                      className="text-river-deep-orange underline underline-offset-2"
                      rel="noreferrer"
                    >
                      river.health/help-center
                    </a>
                    <p className="text-[15px]">Support articles </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {currentTutorial === 'SUMMARY' && (
            <div className="flex flex-col justify-center items-center flex-1 mobile:pt-20 mobile:pb-20">
              <h3 className="font-semibold text-river-deep-orange text-xl mb-10">
                Download Your Benefits Summary.{' '}
              </h3>

              <div className="flex flex-row items-center justify-center w-full tablet:w-[80%] mx-auto space-x-10">
                <div className="w-full tablet:w-[50%]">
                  <h4 className="text-[#0E86C7] font-semibold text-lg mb-1">Benefit Summary</h4>

                  <MarkdownHTML
                    className="w-package-text"
                    content={`Download your benefits summary to learn more about the health services included in your plan. The summary notes limitations on some services that River Health does not cover.<br><br>
                    We **highly recommend** reviewing your benefits summary.`}
                  />
                </div>

                <div className="hidden tablet:block flex-1">
                  <img src={ImageBenefitSummary} alt="error graphic" className="w-auto mx-auto" />
                </div>
              </div>
            </div>
          )}

          <div className="text-center w-full flex flex-col items-center">
            {currentTutorial === 'CONTENTS' && (
              <span className="text-xs mb-3 flex items-center justify-center">
                This will be fast, we promise.
                <Icon id="iconThumbsUp" className="w-5 -mt-2 ml-1" />
              </span>
            )}

            <Button
              label={currentTutorial !== 'SUMMARY' ? 'Continue' : 'Download Summary'}
              onClick={handleTutorialSwitch}
              className="!px-2 mb-3"
            />

            {(currentTutorial === 'INTRO' || currentTutorial === 'SUMMARY') && (
              <button
                type="button"
                onClick={() => handleCloseModal()}
                className="text-river-deep-orange text-xs underline underline-offset-[4px]"
              >
                {currentTutorial !== 'SUMMARY'
                  ? `No, I already know my ${getReadablePlanName(
                      plan?.tier,
                    )} benefits. (not recommended)`
                  : 'Finish, Go to Member Dashboard'}
              </button>
            )}
          </div>

          {currentTutorial === 'INTRO' && (
            <div className="hidden tablet:block">
              <span
                className="w-[150px] h-[150px] border border-river-deep-orange bg-transparent 
              rounded-full absolute -bottom-[90px] -right-[80px]"
              />
              <span
                className="w-[150px] h-[150px] border border-river-deep-orange bg-transparent 
              rounded-full absolute -bottom-[120px] right-[0px]"
              />
            </div>
          )}

          {currentTutorial !== 'INTRO' && (
            <div className="hidden tablet:block">
              <span
                className="w-[150px] h-[150px] border  border-dashed border-river-deep-orange bg-transparent 
              rounded-full absolute -bottom-[90px] -left-[80px]"
              />
              <span
                className="w-[150px] h-[150px] border  border-dashed border-river-deep-orange bg-transparent 
              rounded-full absolute -bottom-[120px] left-[0px]"
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

const BENEFIT_SUMMARIES = {
  essentials: [
    {
      topic: 'Prescriptions',
      steps: [
        {
          title: 'What’s Included?',
          features: [
            { inclusive: true, text: '$0 for medications on the formulary.' },
            { inclusive: true, text: '$0 for Birth control on the formulary.' },
            { inclusive: true, text: '$0 refills for formulary drugs.' },
            { inclusive: true, text: '$0 test strips for diabetes with free shipping.' },
            {
              inclusive: true,
              text: '$0 local pharmacy pick up. (limited to formulary medications).',
            },
          ],
          limitations: undefined,
          graphic: ImagePrescriptionDelivery,
        },
        {
          title: 'What’s Included? (continued)',
          features: [
            { inclusive: true, text: '$0 shipping on prescriptions delivered to your home.' },
            {
              inclusive: false,
              text: 'Prescriptions are at the discretion of the medical provider, and a treatment plan.',
            },
            { inclusive: false, text: 'Not all formulary drugs can be airmailed.' },
          ],
          limitations: undefined,
          graphic: ImagePrescriptionDelivery,
        },
      ],
    },
    {
      topic: 'Lab Testing',
      steps: [
        {
          title: 'What’s Included?',
          features: [
            {
              inclusive: true,
              text: '$0 lab testing through virtual care, including BMP/CMP, CBC,TSH, Hemoglobin A1C, Lipid Panel, STI/STD testing for chlamydia,gonorrhea, HIV, syphilis, etc.',
            },
            {
              inclusive: true,
              text: '$0 in-person lab testing at all Quest Diagnostics locations (requires a lab order form from River Health).',
            },
            {
              inclusive: true,
              text: '$0 at-home lab testing kit available through virtual care.',
            },
          ],
          limitations: undefined,
          graphic: ImageLabTesting,
        },
        {
          title: 'What’s Included? (continued)',
          features: [
            {
              inclusive: true,
              text: '$0 preventative cancer screening: limited to pap smear test,colon cancer screening lab test, and prostate cancer screening lab test.',
            },
            {
              inclusive: false,
              text: 'Lab orders from in-person office visits must be faxed or emailed to River Health. Fax: [+1 218-319-4638](tel:+1218-319-4638). Email: [team@helloriver.com.](mailto:team@helloriver.com)',
            },
          ],
          limitations: undefined,
          graphic: ImageLabTesting,
        },
      ],
    },
    {
      topic: 'Imaging',
      steps: [
        {
          title: 'What’s Included?',
          features: [
            {
              inclusive: true,
              text: 'Onsite in-network primary care clinic/urgent care imaging for standard x-rays.',
            },
            { inclusive: true, text: 'Routine mammograms as part of well woman exams.' },
            {
              inclusive: true,
              text: 'Discounts on MRI, PET/MRI, PET/CT, and ultrasound from partner imaging centers.',
            },
            {
              inclusive: false,
              text: 'Discounts for imaging services are offered by 3rd-party partner imaging centers. Discounts may vary.',
            },
          ],
          limitations: undefined,
          graphic: ImageImagingTesting,
        },
      ],
    },
    {
      topic: 'Virtual Primary Care',
      steps: [
        {
          title: 'What’s Included?',
          features: [
            {
              inclusive: true,
              text: '$0 unlimited access to your dedicated River Health primary care provider via text, audio, and video visits.',
            },
            {
              inclusive: true,
              text: '$0 treatment for acute conditions, e.g., infections, STI treatments, birth control, allergies, skin conditions, colds, sick visits, and more.',
            },
            {
              inclusive: true,
              text: '$0 medication management, refills, and lab orders.',
            },
          ],
          limitations: undefined,
          graphic: ImageVirtualCare,
        },
        {
          title: 'What’s Included? (continued)',
          features: [
            {
              inclusive: true,
              text: 'Virtual chronic condition management for diabetes and hypertension.',
            },
            {
              inclusive: false,
              text: 'In-person referrals will be made if medically appropriate.',
            },
            {
              inclusive: false,
              text: 'Unlimited virtual care is only available through River Health providers.',
            },
          ],
          limitations: undefined,
          graphic: ImageVirtualCare,
        },
      ],
    },
    {
      topic: 'In-Person Doctor Visit',
      steps: [
        {
          title: 'What’s Included?',
          features: [
            {
              inclusive: true,
              text: '$0 preventative care visits, i.e., annual physical, well-woman exam, well-child exam.',
            },
            {
              inclusive: true,
              text: '$0 In-network primary care, OBGYN, dermatology, or urgent care visits.',
            },
            {
              inclusive: true,
              text: '$0 Pap smears.',
            },
          ],
          limitations: undefined,
          graphic: ImageInPersonVisit,
        },
        {
          title: 'What’s Included? (continued)',
          features: [
            {
              inclusive: true,
              text: '$0 preventative vaccinations for Influenza, COVID-19, Tdap, and Td.',
            },
            {
              inclusive: undefined,
              text: '<br>Visit any primary care provider. A primary care provider is a Nurse Practitioner, Physician Assistant or Physician that practices Family Medicine, Internal Medicine, Gynecology, General Medicine, or Pediatrics.',
            },
          ],
          limitations: undefined,
          graphic: ImageInPersonVisit,
        },
      ],
    },
    {
      topic: 'Behavioral Health',
      steps: [
        {
          title: 'What’s Included?',
          features: [
            {
              inclusive: true,
              text: '$0 mental healthcare and therapy sessions from board-certified mental health professionals.',
            },
            {
              inclusive: true,
              text: '$0 behavioral health medication from the River Health formulary.',
            },
            { inclusive: false, text: 'Requires upgrade to the Complete Care Plan.' },
            { inclusive: false, text: 'Up to 24 $0 therapy sessions per year.' },
            { inclusive: false, text: 'Limitation on certain controlled substances.' },
          ],
          limitations: undefined,
          graphic: ImageBehavioralHealth,
        },
      ],
    },
    // {
    //   topic: 'Family Plan',
    //   steps: [
    //     {
    //       title: 'What’s Included?',
    //       features: [
    //         {
    //           inclusive: true,
    //           text: 'All the benefits of the primary care plan including; Unlimited Virtual Care, $0 Labs, $0Prescriptions and more',
    //         },
    //         {
    //           inclusive: true,
    //           text: '$0 Office Visits (Up to 3 $0 office visits per individual).',
    //         },
    //       ],
    //       limitations: [
    //         'Your first three office visits every year are $0, and $99 for subsequent visits.',
    //       ],
    //       graphic: ImageFamilyPlan,
    //     },
    //   ],
    // },
    {
      topic: 'Wellness Service',
      steps: [
        {
          title: 'What’s Included?',
          features: [
            {
              inclusive: undefined,
              text: `As a River Health member, you get free access to the Calm app for meditation, sleep, and relaxation. <br><br>No cost, no stress. Just calm. Use it to unwind after a long day, fall asleep faster, or build a daily mindfulness habit. <br><br>Enjoy guided meditations, relaxing music, and sleep stories for all ages.`,
            },
          ],
          limitations: undefined,
          graphic: ImageWellnessService,
        },
      ],
    },
  ],
  virtual: [
    {
      topic: 'Prescriptions',
      steps: [
        {
          title: 'What’s Included?',
          features: [
            { inclusive: true, text: '$0 for medications on the formulary.' },
            { inclusive: true, text: '$0 for Birth control on the formulary.' },
            { inclusive: true, text: '$0 refills for formulary drugs.' },
            { inclusive: true, text: '$0 test strips for diabetes with free shipping.' },
            {
              inclusive: true,
              text: '$0 local pharmacy pick up. (limited to formulary medications).',
            },
          ],
          limitations: undefined,
          graphic: ImagePrescriptionDelivery,
        },
        {
          title: 'What’s Included? (continued)',
          features: [
            { inclusive: true, text: '$0 shipping on prescriptions delivered to your home.' },
            {
              inclusive: false,
              text: 'Prescriptions are at the discretion of the medical provider, and a treatment plan.',
            },
            { inclusive: false, text: 'Not all formulary drugs can be airmailed.' },
          ],
          limitations: undefined,
          graphic: ImagePrescriptionDelivery,
        },
      ],
    },
    {
      topic: 'Virtual Primary Care',
      steps: [
        {
          title: 'What’s Included?',
          features: [
            {
              inclusive: true,
              text: '$0 unlimited access to your dedicated River Health primary care provider via text, audio, and video visits.',
            },
            {
              inclusive: true,
              text: '$0 treatment for acute conditions, e.g., infections, STI treatments, birth control, allergies, skin conditions, colds, sick visits, and more.',
            },
            {
              inclusive: true,
              text: '$0 medication management, refills, and lab orders.',
            },
          ],
          limitations: undefined,
          graphic: ImageVirtualCare,
        },
        {
          title: 'What’s not included? (continued)',
          features: [
            {
              inclusive: true,
              text: 'Virtual chronic condition management for diabetes and hypertension.',
            },
            {
              inclusive: false,
              text: 'In-person referrals will be made if medically appropriate.',
            },
            {
              inclusive: false,
              text: 'Unlimited virtual care is only available through River Health providers.',
            },
          ],
          limitations: undefined,
          graphic: ImageVirtualCare,
        },
      ],
    },
  ],
  complete: [
    {
      topic: 'Prescriptions',
      steps: [
        {
          title: 'What’s Included?',
          features: [
            { inclusive: true, text: '$0 for medications on the formulary.' },
            { inclusive: true, text: '$0 for Birth control on the formulary.' },
            { inclusive: true, text: '$0 refills for formulary drugs.' },
            { inclusive: true, text: '$0 test strips for diabetes with free shipping.' },
            {
              inclusive: true,
              text: '$0 local pharmacy pick up. (limited to formulary medications).',
            },
          ],
          limitations: undefined,
          graphic: ImagePrescriptionDelivery,
        },
        {
          title: 'What’s Included? (continued)',
          features: [
            { inclusive: true, text: '$0 shipping on prescriptions delivered to your home.' },
            {
              inclusive: false,
              text: 'Prescriptions are at the discretion of the medical provider, and a treatment plan.',
            },
            { inclusive: false, text: 'Not all formulary drugs can be airmailed.' },
          ],
          limitations: undefined,
          graphic: ImagePrescriptionDelivery,
        },
      ],
    },
    {
      topic: 'Virtual Primary Care',
      steps: [
        {
          title: 'What’s Included?',
          features: [
            {
              inclusive: true,
              text: '$0 unlimited access to your dedicated River Health primary care provider via text, audio, and video visits.',
            },
            {
              inclusive: true,
              text: '$0 treatment for acute conditions, e.g., infections, STI treatments, birth control, allergies, skin conditions, colds, sick visits, and more.',
            },
            {
              inclusive: true,
              text: '$0 medication management, refills, and lab orders.',
            },
          ],
          limitations: undefined,
          graphic: ImageVirtualCare,
        },
        {
          title: 'What’s not included? (continued)',
          features: [
            {
              inclusive: true,
              text: 'Virtual chronic condition management for diabetes and hypertension.',
            },
            {
              inclusive: false,
              text: 'In-person referrals will be made if medically appropriate.',
            },
            {
              inclusive: false,
              text: 'Unlimited virtual care is only available through River Health providers.',
            },
          ],
          limitations: undefined,
          graphic: ImageVirtualCare,
        },
      ],
    },
    {
      topic: 'Behavioral Health',
      steps: [
        {
          title: 'What’s Included?',
          features: [
            {
              inclusive: true,
              text: '$0 mental healthcare and therapy sessions from board-certified mental health professionals.',
            },
            {
              inclusive: true,
              text: '$0 behavioral health medication from the River Health formulary.',
            },
            { inclusive: false, text: 'Requires upgrade to the Complete Care Plan.' },
            { inclusive: false, text: 'Up to 24 $0 therapy sessions per year.' },
            { inclusive: false, text: 'Limitation on certain controlled substances.' },
          ],
          limitations: undefined,
          graphic: ImageBehavioralHealth,
        },
      ],
    },
    {
      topic: 'Lab Testing',
      steps: [
        {
          title: 'What’s Included?',
          features: [
            {
              inclusive: true,
              text: '$0 lab testing through virtual care, including BMP/CMP, CBC,TSH, Hemoglobin A1C, Lipid Panel, STI/STD testing for chlamydia,gonorrhea, HIV, syphilis, etc.',
            },
            {
              inclusive: true,
              text: '$0 in-person lab testing at all Quest Diagnostics locations (requires a lab order form from River Health).',
            },
            {
              inclusive: true,
              text: '$0 at-home lab testing kit available through virtual care.',
            },
          ],
          limitations: undefined,
          graphic: ImageLabTesting,
        },
        {
          title: 'What’s Included? (continued)',
          features: [
            {
              inclusive: true,
              text: '$0 preventative cancer screening: limited to pap smear test,colon cancer screening lab test, and prostate cancer screening lab test.',
            },
            {
              inclusive: false,
              text: 'Lab orders from in-person office visits must be faxed or emailed to River Health. Fax: [+1 218-319-4638](tel:+1218-319-4638). Email: [team@helloriver.com.](mailto:team@helloriver.com)',
            },
          ],
          limitations: undefined,
          graphic: ImageLabTesting,
        },
      ],
    },
  ],
  legacy: [],
};

WelcomePackage.defaultProps = {
  isAlreadyEnrolled: false,
  plan: undefined,
  sponsor: undefined,
};

export default WelcomePackage;
